<template>
  <div />
</template>

<script>
import Realisation from '@/models/Realisation';

import dot from 'dot-object';
import { mapGetters, mapMutations } from 'vuex';
import { fetchAssetByLocationId } from '@/services/processes'
import Vue from 'vue';
const uuid = require('uuid');
import { generateFullAddress } from '@/config'

export default {
  name: 'BaseRealisationStep',
  data() {
    return {
      uuid: null,
      disabled: false,

      /**
       * A flag that is briefly set when cancelling a change of an already completed step
       *  It's used to reload the components by briefly changing their visibility
       */
      reloading: false,


      category: null,

      /**
       * Whether the form is active.
       *  It's not when the step has already been completed, and the user didn't explicitly activate the form.
       */
      locked: true,

      /**
       * Whether the step details are being corrected (meaning the progress should remain unaffected upon saving data)
       */
      correction: false,

      /**
       * Whether we're saving before going to the next step
       */
      goingToNextStep: false,
      redirectToAfterSubmit: null,

      /**
       *
       */
      feedback: null,
      feedbackMessage: '',

      /**
       * Docs = docs belonging to active Location, by doctype
       * RealistionDocs = docs belonging to active Realisation process, by doctype
       */
      Docs: {},
      RealisationDocs: {},
      address: {
        CityName: 'city',
        HouseNumber: 'number',
        HouseNumberSuffix: 'suffix',
        PostalCode: 'postalcode',
        StreetName: 'street'
      },
      lastSavedRequestUuids: [],
      isRecordLocked: false,
      changeStarted: false,
      stepGuardDeferred: false
    };
  },
  computed: {
    ...mapGetters('requests', ['requestByUuid']),
    ...mapGetters('realisations', ['hasRecord', 'recordByUuid', 'getSelectedRecord']),
    ...mapGetters('relations', ['changeCount', 'requestUuidsByRealisationUuid']),
    ...mapGetters('user', ['hasRole', 'canAccessRequests', 'canAccessAssetManagement']),
    ...mapGetters('tenant', [
      'getWorkflowUuidByName',
      'isCurrentTenant',
      'getEnabledContractors',
      'getWorkflowSpecByName',
      'getActiveProcessTypes',
      'getSteps'
    ]),
    ...mapGetters('chargingpoints', ['getChargingPointByUUID']),
    ...mapGetters('realisation', ['getActiveRecordValues']),
    ...mapGetters('assets', ['getAssets']),
    ...mapGetters('processes', ['getSelectedProcess', 'getSelectedAsset', 'getDisableSaveRecord']),

    steps() {
      return this.getSteps
    },
    stepsByUuid() {
      return this.steps.reduce((result, step) => {
        result[step.uuid] = step;
        return result;
      }, {});
    },
    routeUuid() {
      return this.$route.params.uuid;
    },

    selectedProcessType () {
      return this.getActiveProcessTypes.find(type => type.value === 'realisation')
    },

    /**
     * Create a new record if the requested record cannot be found
     */
    record() {
      const baseRecord = new Realisation({
        ref: null,
        data: {
          uuid: this.uuid,
          processType: this.selectedProcessType
        }
      });
      return this.getSelectedRecord || baseRecord
    },

    isNewProcess () {
      return this.$route.name?.startsWith('New')
    },

    /**
     * Whether or not the Realisation Process has been saved
     */
    isUnsavedRecord() {
      return (this.record.ref || null) === null;
    },

    hasCategory() {
      return (this.record.category || null) !== null;
    },

    /**
     * The active step of the realisation proces
     */
    step() {
      if (!this.record) return 1;

      return this.currentStep
    },

    /**
     * Get the active & current step data from the configuration
     */
    currentStepData() {
      this.$store.dispatch('realisations/updateCurrentStep', this.currentStep);
      return this.getStepDataByNumber({ number: this.currentStep });
    },

    /**
     * The location details
     */
    values() {
      try {
        return this.getSelectedRecord?.CurrentLocation || this.record.values.Location;
      } catch (e) {
        return {
          Location: {}
        };
      }
    },
    CurrentLocation() {
      return this.values;
    },

    activeProcessStep () {
      return this.record.processStatus?.currentStep
    },

    currentStep () {
      const step = this.record.processStatus?.currentStep
      const finalStep = this.getSteps?.at(-1)?.step
      const currentStep = (step === 100 ? finalStep : step) || 1

      return +this.$route.params?.step || currentStep
    },

    /**
     * Location version
     */
    version() {
      try {
        return this.values.version;
      } catch (e) {
        return 1;
      }
    },

    /**
     * The related requests UUIDS
     */
    requestUuids() {
      const requestForNewProcess = this.$route.query?.requests
      return requestForNewProcess
        ? [requestForNewProcess]
        : this.requestUuidsByRealisationUuid({ uuid: this.record.uuid });
    },

    /**
     * The related request objects
     */
    connectedRequests() {
      return this.requestUuids.slice().map(uuid => {
        return this.requestByUuid({ uuid });
      });
    },

    isAmsterdam () {
      return process.env.VUE_APP_TENANT === 'amsterdam'
    },

    /**
     * Whether the step can still be changed
     */
    openForChange() {
      return (
        this.record.status.completed === false &&
        this.record.status.cancelled !== true &&
        this.record.status.onhold !== true
      );
    },

    /**
     * Whether the CPO Advice should be / was skipped
     *  This is due to the current location being suggested by the CPO
     */
    Skip() {
      return !!this.values.CPOAdvice.Skip;
    },
    completed() {
      return !!this.record.status.completed;
    },
    statusLocked() {
      return !!this.record.status.locked;
    },
    onhold() {
      return !!this.record.status.onhold;
    },
    cancelled() {
      return !!this.record.status.cancelled;
    },
    cancelledOrOnHold() {
      return this.onhold || this.cancelled;
    },
    disableNextBtn() {
      return !this.feedback && this.cancelledOrOnHold && !this.completedUuids.includes(this.currentStepData.uuid);
    },

    /**
     * Access
     */
    mayEdit() {
      if (this.isRecordLocked) {
        return false
      }
      return !this.statusLocked && (this.hasRole({ role: 'admin' }) || this.currentStepData.access.edit.some(role => this.hasRole({ role })));
    },

    /**
     * Record Flow State
     */

    recordState() {
      try {
        return this.record.flow.state;
      } catch (e) {
        return [];
      }
    },
    completedUuids() {
      return this.recordState.filter(state => state.completed_at).map(state => state.uuid);
    },
    availableUuids() {
      return this.recordState.filter(state => state.started_at).map(state => state.uuid);
    },
    isCompletedStep() {
      if (this.stepMixin()) {
        return this.stepMixin().isCompletedStep
      }
      return this.completedUuids.includes(this.currentStepData?.uuid);
    },
    isAvailableStep() {
      return this.availableUuids.includes(this.currentStepData?.uuid);
    }
  },
  watch: {
    routeUuid(newValue) {
      if (newValue !== null) {
        this.uuid = newValue;
      }
      this.clearChanges();
    },
    hasRecord() {
      if (!this.hasRecord) {
        this.redirectToStart();
      }
    },
    record() {
      this.locked = !this.changeStarted && (!this.mayEdit || this.isCompletedStep || this.completed || this.onhold || this.cancelled);

      if (this.record.ref) {
        this.RealisationDocs = this.record.Docs;
      } else {
        this.redirectToStart();
      }
    },
    getSelectedRecord: {
      deep: true,
      handler (record) {
        if (record) {
          this.record.Docs = record.Docs
        }
      }
    },
    'record.uuid': {
      handler () {
        if (!this.stepGuardDeferred) return;

        this.stepGuardDeferred = false;
        this.guardStepAgainstRecord();
      }
    },
    changeCount: {
      handler() {
        if (this.changeCount !== 0) {
          this.storeRelationChanges({ record: {} });
        }
      }
    },
    getDisableSaveRecord: {
      handler (value) {
        this.disabled = value
      }
    },
    locked: {
      immediate: true,
      handler (locked) {
        this.$store.dispatch('processes/setRecordLocked', { locked })
      }
    }
  },
  async created() {
    this.uuid = this.routeUuid || 'new';

    const record = JSON.parse(JSON.stringify(this.record));
    this.$store.dispatch('realisation/setSavedRecordValues', { values: record.values.Location });

    this.category = this.record.category
    // Temorary, until the migration of record lock is done //
    if (this.canAccessAssetManagement && this.record.status.completed && this.record.locationId) {
      this.handleProcessLocking ()
    }

    if (!this.getSelectedRecord) return;

    // Reset process store entries //
    if (!this.record.status.completed && !this.record.locationId) {
      this.$store.dispatch('processes/setSelectedProcess', { process: null })
      this.$store.dispatch('processes/setSelectedAsset', { asset: null })
    }

    if (!this.record) {
      // console.log("no record")
      this.redirectToStart();
    } else {
      this.stepGuardDeferred = !this.guardStepAgainstRecord();
    }
  },
  beforeDestroy() {
    this.clearChanges();
  },
  methods: {
    ...mapMutations('realisations', ['addRecord', 'replaceRecord', 'updateRecordLocation', 'setLastOpenedRecord']),
    ...mapMutations('relations', ['setConnectionChange', 'finalizeChanges', 'clearChanges', 'resetRelations']),
    ...mapMutations('chargingpoints', ['replaceLocation', 'resetLocationStatus']),

    getStepDataByNumber({ number }) {
      return this.steps.find(step => (step.step || step.short) == number) || null;
    },

    stepMixin () {
      return this.$children.find(mixin => mixin.$vnode.tag.includes('Step'))
    },

    mapMixin () {
      return this.$children.find(mixin => mixin.$vnode.tag.includes('Map'))
    },

    async saveRecord () {
      if (this.mapMixin()?.correction) {
        const mapForm = this.mapMixin().$refs.form
        mapForm.validate()
        if (!mapForm.isValid()) {
          this.handleValidationError();
          return;
        }
      }

      if (this.stepMixin()) {
        if (this.isNewProcess) {
          // Validate fields on save by new process //
          const form = this.stepMixin().$refs.form
          form.validate()
          if (!form.isValid()) {
            this.handleValidationError();
            return;
          }
        }
        await this.stepMixin().handleSubmit()
      } else {
        await this.handleSubmit()
      }
    },

    async handleProcessLocking () {
      // Locked if not the last completed process within the asset //
      let asset = this.getSelectedAsset

      if (!asset || (this.record.locationId && asset.locationId !== this.record.locationId.full)) {
        const token = await this.$auth.getTokenSilently();
        const response = await fetchAssetByLocationId({ token, locationId: this.record.locationId?.full })
          .catch(() => console.log(`Asset with locationId ${this.record.locationId?.full} not found!`))

        asset = response?.asset
      }

      if (asset && this.record.locationId) {
        // If there is an ongoing asset process, lock the realisation //
        this.isRecordLocked = asset.processes.length > 1
      }
    },

    guardStepAgainstRecord() {
      if (!this.getSelectedRecord || this.record?.uuid !== this.routeUuid) return false;

      if (this.isBeyondCurrentStep()) {
        // console.log("Don't think so!", this.step, this.currentStep)
        this.redirectToStep({ step: this.activeProcessStep });
      } else {
        this.setLastOpenedRecord({ uuid: this.uuid });
      }

      return true;
    },

    redirectToStart() {
      if (this.currentStep === 1) return; // Already there...
      const record = this.getSelectedRecord //this.recordByUuid({ uuid: this.uuid }) || ;
      if (record) {
        this.$router.push({
          name: 'RealisationStep1',
          params: {
            uuid: this.uuid,
            step: 1
          }
        });
      }
    },
    handleNext() {
      if (this.record.ref && this.uuid !== 'new') {
        const stepState = this.record.nextAvailableFlowStep({ currentUuid: this.currentStepData.uuid });
        this.redirectToStep({ step: this.stepsByUuid[stepState.uuid].short });
      }
    },
    handlePrevious() {
      // TODO: currentStep is no longer a number...
      let stepState = this.record.previousAvailableFlowStep({ currentUuid: this.currentStepData.uuid });
      this.redirectToStep({ step: this.stepsByUuid[stepState.uuid].short });
    },
    async redirectToStep({ step }) {
      const record = this.getSelectedRecord //this.recordByUuid({ uuid: this.uuid });

      if (record) {
        await this.$router.replace({
          name: `realisationStep${step}`,
          params: {
            uuid: this.uuid,
            step
          }
        }).catch(e => console.log(e));
      }
    },
    handleInput({ name, value }) {
      const path = name.split('.'),
        last = path.pop();

      path.reduce((o, k) => (o[k] = o[k] || {}), this.getActiveRecordValues)[last] = value;

      this.$store.dispatch('realisation/setActiveRecordValues', { values: this.getActiveRecordValues });

      if (this.getSelectedRecord) {
        this.getSelectedRecord.CurrentLocation = this.getActiveRecordValues
        this.$store.dispatch('realisations/setSelectedRecord', { record: this.getSelectedRecord })
      }
      this.$emit('handleInput', { name, value })
    },
    /**
     * Submit and go to the next step
     */
    async handleSubmitAndNext() {
      if (this.mapMixin()) {
        const mapForm = this.mapMixin().$refs.form
        mapForm.validate()
        if (!mapForm.isValid()) {
          this.handleValidationError();
          return;
        }
      }

      if (this.stepMixin()) {
        return this.stepMixin().handleSubmitAndNext()
      }

      const form = this.$refs.form
      form.validate();

      if (!form.isValid()) {
        this.handleValidationError();
        return;
      }

      this.goingToNextStep = true;
      let success = await this.handleSubmit();
      this.goingToNextStep = false;

      if (success) {
        if (this.redirectToAfterSubmit) {
          this.redirectToStep({ step: this.redirectToAfterSubmit });
        } else {
          this.handleNext();
        }
      }
    },

    // Stub
    async handleComplete() {
      if (this.mapMixin()) {
        const mapForm = this.mapMixin().$refs.form
        mapForm.validate()
        if (!mapForm.isValid()) {
          this.handleValidationError();
          return;
        }
      }

      if (this.stepMixin()) {
        return this.stepMixin().handleComplete()
      }

      this.goingToNextStep = true
      await this.handleSubmit()
      this.goingToNextStep = false
    },

    /**
     * Handle Validation Error
     */
    handleValidationError() {
      this.feedback = 'danger';
      this.feedbackMessage = this.$t('BaseRealisationStep.validation.checkAllFields');
      this.$emit('feedback', { feedback: this.feedback, message: this.feedbackMessage })
    },

    clearDangerFeedback() {
      if (this.feedback === 'danger') {
        this.feedback = null;
        this.feedbackMessage = '';
        this.$emit('feedback', { feedback: null, message: '' });
      }
    },

    handleFileChanges({ doctype, files }) {
      // this.Docs[doctype] = files
      Vue.set(this.Docs, doctype, files);
    },

    /**
     * Stub
     */
    prepIntention({ step, correction, complete, target }) {
      return { step, correction, complete, target };
    },

    /**
     * Stub
     */
    prepData({ data }) {
      return data;
    },

    /**
     * Stub
     */
    isValidData () {
      return this.isAmsterdam ? true : { valid: true };
    },

    prepStatus() {
      // { data }
      // TODO: Maybe remove this method, now that this status has been replaced with Intention?
      return {};
    },

    postSave() {
      this.$store.dispatch('mail/refreshMailLogs', Date.now())
    },

    mergeLocationData({ data }) {
      data.Location = this.CurrentLocation.Location;
      return data;
    },

    async changeChargingpointStatus({ response }) {
      const uuid = response.data.record.process?.Location?.chargingpointUuid;
      await this.$store.dispatch('chargingpoints/changeChargingpointStatus', {
        uuid,
        status: 'realized' // Step 8 completed
      });
    },

    /**
     * Handle Submit event
     */
    async handleSubmit() {
      try {
        let intention = this.prepIntention({
          step: this.currentStepData.uuid,
          correction: this.correction,
          complete: this.goingToNextStep,
          target: null // TODO: implement?
        });

        this.feedback = null;
        this.feedbackMessage = '';
        this.disabled = true;

        let data;

        const form = this.$refs.form;
        const values = form.getValues();

        if (this.record.ref) {
          data = this.currentStep === 1 ? this.getActiveRecordValues : this.getSelectedRecord.CurrentLocation;
          data.requestUuids = this.requestUuids;
        } else {
          // Get & tranform form input
          data = this.getSelectedRecord?.CurrentLocation || dot.object(values);
          // If location is selected, check if chargingpointUuid is present //
          const { chargingpointUuid, chargingpointStatus } = this.getActiveRecordValues?.Location || {}
          if (!data.Location?.chargingpointUuid && chargingpointUuid) {
            data.Location.chargingpointUuid = chargingpointUuid
            data.Location.chargingpointStatus = chargingpointStatus
          }
        }

        data = this.prepData({ data });

        // Version
        data.version = this.version;

        // Get the record reference, or add the uuid in case of a new record
        const ref = this.record.ref || false;
        if (ref === false) {
          data.uuid = uuid.v4();
          data.processType = this.selectedProcessType
          data.category = this.category
        }

        data.step = this.currentStepData.uuid;

        data.status = this.prepStatus({ data });
        data.correction = this.correction;

        if (this.goingToNextStep || this.correction) {
          const validStepData = this.isValidData({ ref, data })
          if (this.isAmsterdam) {
            if (!validStepData) {
              this.disabled = false;
              return false;
            }
          } else {
            if (!validStepData.valid) {
              // console.log("invalid data")
              // Note: The validation method should set the feedback
              this.$emit('feedback', validStepData)
              this.disabled = false;
              return false;
            }
          }
        }


        this.$emit('isSaving', true)

        // Temp: Generate full address field for Mongo indexing //
        if (data.Location?.StreetName) {
          const code = this.record.MunicipalityCode || this.record.Municipality
          data.Location.FullAddress = generateFullAddress({ location: data.Location, code })
        }
        // console.log(this.currentStep, data, this.getActiveRecordValues)
        // if (data !== 'sarma') {
        //   this.$emit('isSaving', false)
        //   return
        // }

        // set the proper endpoint
        let endpoint = '/api/realisation_step';
        if (!ref) {
          endpoint = '/api/realisation_create';

          data.flowUuid = this.getWorkflowUuidByName({
            name: 'realisation'
          });
        }

        // Make the call
        const token = await this.$auth.getTokenSilently();
        const api = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            ref,
            data,
            intention
          })
        });
        if (!api.ok) {
          if (api.status === 401) {
            this.feedbackMessage =
              'U heeft niet de rechten om deze handeling uit te voeren. Mogelijk is uw sessie verlopen.';
          }
          if (api.status === 400) {
            this.feedbackMessage = 'De invoer is ongeldig verklaard. Neem contact op indien dit zich blijft voordoen.';
          }

          this.$emit('isSaving', false)
          throw new Error(api.error);
        }
        const response = await api.json();

        this.finalizeChanges();
        this.clearChanges();
        this.resetRelations()
        this.$emit('isSaving', false)

        const record = new Realisation({ data: response.data.record, ref: response.data._id });
        this.$emit('currentRecord', record)
        this.$emit('feedback', { feedback: null, message: '' })

        if (ref) {
          this.replaceRecord({ record: response.data.record });
          this.$store.dispatch('realisation/setSavedRecordValues', { values: record.values.Location });

          if (record.status.completed && this.getAssets) { // If assets are loaded in the store //
            const asset = response.data.record.asset

            if (asset && this.getAssets.some(location => location.uuid === asset.uuid)) {
              this.$store.dispatch('assets/replaceAsset', { asset })
            } else {
              this.$store.dispatch('assets/addAssetProcess', { asset })
            }
          }

        } else {
          this.$store.dispatch('realisations/addNewRecord', { record: response.data.record });

          const [linkedChargingpoint] = response.data.locations;
          if (linkedChargingpoint) {
            if (!this.getChargingPointByUUID({ uuid: linkedChargingpoint.uuid })) {
              await this.$store.dispatch('chargingpoints/putCreatedChargingpoint', linkedChargingpoint);
            }
          }

          if (this.goingToNextStep || this.correction) {
            this.$router.push({ name: 'realisationStep2', params: { uuid: data.uuid, step: '2' } });
          } else {
            this.$router.replace({ name: 'realisationStep1', params: { uuid: data.uuid, step: '1' } });
          }
          // if create process is initiated from request //
          if (this.requestUuids.length) {
            const data = {
              type: 'add',
              requestUuid: this.requestUuids[0],
              realisationUuid: response.data.record.uuid
            }
            this.$store.dispatch('relations/setRelation', { data })
          }
        }

        this.$store.dispatch('realisations/setSelectedRecord', { record: response.data.record })

        if (Array.isArray(response.data.locations)) {
          response.data.locations.forEach(location => {
            this.replaceLocation({ location });
          });
        }

        this.updateRecordLocation({ record: response.data.record });
        if (this.record.status.completed && !this.correction) {
          this.changeChargingpointStatus({ response });
        }
        // After creation of a new RP from RQ, bind RQ to RP //
        if (this.requestUuids.length) {
          this.storeRelationChanges({ record })
        }

        this.$store.dispatch('tenant/setToast', {
          vm: this,
          message: this.$i18n.t('Process.processSaved'),
          error: false,
          delay: 100
        });

        this.postSave();
        this.canAddMapLocation = false;
        this.disabled = false;
        this.correction = false
        //this.$emit('handleCancelChange')
        this.handleCancelChange()
        return true;
      } catch (e) {
        console.log(e, e.message);

        this.disabled = false;
        this.feedback = 'danger';
        this.feedbackMessage = this.feedbackMessage || e.message || this.$i18n.t('Process.generalSaveError');
        return false;
      }
    },
    /**
     * Add / Remove Requests to Realisation
     */
    addRequestsToRealisation() {
      if (this.record && this.requestAdditions) {
        this.requestAdditions.forEach(uuid => {
          this.setConnectionChange({
            type: 'add',
            requestUuid: uuid,
            realisationUuid: this.record.uuid
          });
        });
      }
    },
    handleBreakConnection({ uuid }) {
      this.setConnectionChange({
        type: 'remove',
        requestUuid: uuid,
        realisationUuid: this.record.uuid
      });
    },

    async storeRelationChanges ({ record }) {
      try {
        const ref = this.record.ref || record.ref || false;

        if (!this.canAccessRequests) return;

        this.feedback = null;
        this.feedbackMessage = '';

        if (!ref) return;

        const data = {
          step: 'relations',
          requestUuids: this.requestUuids
        };

        // Make the call
        const token = await this.$auth.getTokenSilently();
        const api = await fetch('/api/realisation_relations', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            ref,
            data
          })
        });
        if (!api.ok) {
          // console.log(api.status)
          if (api.status === 401) {
            this.feedbackMessage =
              'U heeft niet de rechten om deze handeling uit te voeren. Mogelijk is uw sessie verlopen.';
          }
          if (api.status === 400) {
            this.feedbackMessage = 'De invoer is ongeldig verklaard. Neem contact op indien dit zich blijft voordoen.';
          }
          // console.log(api)
          throw new Error('Unable to store connection');
        }
        const response = await api.json();

        this.finalizeChanges();
        this.clearChanges();

        this.$nextTick(() => {
          if (ref) {
            this.$store.dispatch('realisations/setSelectedRecord', { record: response.data.record })
          }
        })

      } catch (e) {
        console.log(e);
        this.feedback = 'danger';
        this.feedbackMessage =
          this.feedbackMessage || 'De aanvraag kon niet verbonden worden. Probeer het nog een keer.';
        this.clearChanges();
      }
    },
    handleStartChange() {
      this.locked = false;
      this.correction = true
      this.changeStarted = true
      if (this.stepMixin()) {
        ['stepMixin', 'mapMixin'].forEach(mixin => {
          this[mixin]().locked = false
          this[mixin]().changeStarted = true
          //this[mixin]().correction = true
        })
      }
    },
    handleCancelChange() {
      //this.reloading = true;
      if (this.isNewProcess) {
        return;
      }
      this.$nextTick(() => {
        //this.reloading = false;
        this.correction = false;
        this.changeStarted = false

        if (this.$route.params?.step && this.activeProcessStep !== +this.$route.params.step) {
          this.locked = true;
        }

        if (!this.isNewProcess) {
          this.$emit('isLocationConnected', true);
        }

        // TODO: Three separate mixins: as sson as possible, move all this to store //
        ['stepMixin', 'mapMixin'].forEach(mixin => {
          if (this[mixin]()) {
            this[mixin]().locked = false
            this[mixin]().correction = false
            this[mixin]().changeStarted = false
            this[mixin]().handleCancelChange();

          }
        })
        this.$store.dispatch('processes/setRecordLocked', { locked: true })

        if (this.getSelectedRecord) {
          this.getSelectedRecord.CurrentLocation = JSON.parse(JSON.stringify(this.getSelectedRecord?.raw.process))
          this.$store.dispatch('realisations/setSelectedRecord', { record: this.getSelectedRecord })
        }
      });
    },
    handleStartCorrection() {
      this.correction = true
      this.$store.dispatch('processes/setRecordLocked', { locked: true })
      this.handleStartChange();
      ['stepMixin', 'mapMixin'].forEach(mixin => {
        if (this[mixin]()) {
          this[mixin]().correction = true
          this[mixin]().handleStartChange()
        }
      })
    },

    /**
     * Docs: find highest doctype index
     */
    minIdByDocType({ doctype }) {
      const docs = this.RealisationDocs?.[doctype];
      return docs?.length ? Math.max(...docs.map(doc => doc.id ?? doc)) : 0
    },
    prepDocData({ doctype }) {
      let docs = this.Docs[doctype] || [];

      return docs
        .filter(file => file.progress === 100)
        .map(file => {
          return {
            id: file.id,
            ext: file.ext
          };
        });
    },

    isBeyondCurrentStep() {
      let uuid = this.currentStepData.uuid;
      return !this.availableUuids.includes(uuid);
    }
  }
};
</script>

<style lang="scss">
.RealisationDetail {
  text-align: left;
  padding: 18px 0 30px;

  &__map {
    position: relative;
    max-width: 100%;
    height: 100%;
  }

  &__aside {
    position: sticky !important;
    top: $menu-height;
  }
  &__RequestList {
    list-style: none;
    margin: 0;
    padding: 0;

    a {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 380px;
    }
    .icon {
      cursor: pointer;

      &:hover {
        color: #ec0000;
      }
    }
  }
}
</style>
